import { WeaponId } from '@fisura/sim';

/**
 * Procedurally synthesized sound effects via WebAudio — no audio assets to
 * download or license. Each effect is built from noise bursts / oscillators
 * with short envelopes, tuned per weapon. The AudioContext can only start
 * after a user gesture; call resume() from the pointer-lock click.
 */
export class AudioFx {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private noiseBuf: AudioBuffer | null = null;

  resume(): void {
    if (!this.ctx) {
      this.ctx = new AudioContext();
      this.master = this.ctx.createGain();
      this.master.gain.value = 0.5;
      this.master.connect(this.ctx.destination);
      // 1s of white noise, reused by every shot.
      const len = this.ctx.sampleRate;
      this.noiseBuf = this.ctx.createBuffer(1, len, this.ctx.sampleRate);
      const data = this.noiseBuf.getChannelData(0);
      for (let i = 0; i < len; i++) data[i] = Math.random() * 2 - 1;
    }
    if (this.ctx.state === 'suspended') void this.ctx.resume();
  }

  /** Per-weapon gunshot: filtered noise crack + low thump. */
  shot(weaponId: number): void {
    if (!this.ready()) return;
    const t = this.ctx!.currentTime;
    if (weaponId === WeaponId.Shotgun) {
      this.noise(t, 0.22, 900, 0.5);
      this.thump(t, 70, 0.3, 0.5);
    } else if (weaponId === WeaponId.Rifle) {
      this.noise(t, 0.1, 2600, 0.4);
      this.thump(t, 120, 0.12, 0.3);
    } else {
      // SMG
      this.noise(t, 0.07, 1800, 0.3);
      this.thump(t, 150, 0.08, 0.2);
    }
  }

  /**
   * Another player's gunshot, positioned in stereo. `pan` -1..1 (left/right
   * relative to view), `vol` 0..1 from distance. Distant shots sound duller
   * (lower cutoff), which doubles as a distance cue.
   */
  remoteShot(weaponId: number, pan: number, vol: number): void {
    if (!this.ready() || vol < 0.02) return;
    const t = this.ctx!.currentTime;
    const muffle = 0.45 + 0.55 * vol; // far → duller
    if (weaponId === WeaponId.Shotgun) {
      this.noise(t, 0.2, 800 * muffle, 0.45 * vol, pan);
      this.thump(t, 65, 0.25, 0.45 * vol, pan);
    } else if (weaponId === WeaponId.Rifle) {
      this.noise(t, 0.1, 2400 * muffle, 0.38 * vol, pan);
      this.thump(t, 110, 0.1, 0.25 * vol, pan);
    } else {
      this.noise(t, 0.07, 1700 * muffle, 0.28 * vol, pan);
    }
  }

  /** A nearby player's footstep (stereo-positioned soft tick). */
  footstep(pan: number, vol: number): void {
    if (!this.ready() || vol < 0.03) return;
    const t = this.ctx!.currentTime;
    this.noise(t, 0.045, 500, 0.22 * vol, pan);
  }

  /** Grenade/rocket explosion — big positional boom with a long low tail. */
  explosion(pan: number, vol: number): void {
    if (!this.ready() || vol < 0.02) return;
    const t = this.ctx!.currentTime;
    this.noise(t, 0.5, 700, 0.7 * vol, pan);
    this.thump(t, 55, 0.6, 0.8 * vol, pan);
    this.noise(t + 0.05, 0.7, 220, 0.35 * vol, pan);
  }

  /** Hitmarker tick; brighter two-tone on a kill. */
  hit(lethal: boolean): void {
    if (!this.ready()) return;
    const t = this.ctx!.currentTime;
    this.blip(t, 1200, 0.05, 0.16);
    if (lethal) this.blip(t + 0.07, 1700, 0.09, 0.2);
  }

  /** You took damage: short low thud. */
  damaged(): void {
    if (!this.ready()) return;
    const t = this.ctx!.currentTime;
    this.thump(t, 95, 0.14, 0.35);
    this.noise(t, 0.05, 500, 0.12);
  }

  /** You died: descending tone. */
  death(): void {
    if (!this.ready()) return;
    this.sweep(440, 110, 0.5, 0.25);
  }

  /** Respawned: quick ascending blip. */
  respawn(): void {
    if (!this.ready()) return;
    this.sweep(330, 660, 0.18, 0.15);
  }

  /** Match over fanfare-ish chirp. */
  matchEnd(won: boolean): void {
    if (!this.ready()) return;
    const t = this.ctx!.currentTime;
    if (won) {
      this.blip(t, 523, 0.12, 0.2);
      this.blip(t + 0.13, 659, 0.12, 0.2);
      this.blip(t + 0.26, 784, 0.2, 0.22);
    } else {
      this.sweep(330, 165, 0.5, 0.2);
    }
  }

  reload(): void {
    if (!this.ready()) return;
    const t = this.ctx!.currentTime;
    this.blip(t, 500, 0.04, 0.1);
    this.blip(t + 0.12, 400, 0.04, 0.1);
  }

  // --- primitives --------------------------------------------------------

  private ready(): boolean {
    return !!this.ctx && this.ctx.state === 'running';
  }

  /** Route a node to the master, optionally through a stereo panner. */
  private out(pan?: number): AudioNode {
    if (pan === undefined || Math.abs(pan) < 0.01) return this.master!;
    const p = this.ctx!.createStereoPanner();
    p.pan.value = Math.max(-1, Math.min(1, pan));
    p.connect(this.master!);
    return p;
  }

  private noise(t: number, dur: number, cutoff: number, vol: number, pan?: number): void {
    const ctx = this.ctx!;
    const src = ctx.createBufferSource();
    src.buffer = this.noiseBuf;
    const filter = ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.value = cutoff;
    const g = ctx.createGain();
    g.gain.setValueAtTime(vol, t);
    g.gain.exponentialRampToValueAtTime(0.001, t + dur);
    src.connect(filter).connect(g).connect(this.out(pan));
    src.start(t, Math.random() * 0.5, dur + 0.05);
    src.stop(t + dur + 0.05);
  }

  private thump(t: number, freq: number, dur: number, vol: number, pan?: number): void {
    const ctx = this.ctx!;
    const osc = ctx.createOscillator();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(freq, t);
    osc.frequency.exponentialRampToValueAtTime(Math.max(30, freq * 0.4), t + dur);
    const g = ctx.createGain();
    g.gain.setValueAtTime(vol, t);
    g.gain.exponentialRampToValueAtTime(0.001, t + dur);
    osc.connect(g).connect(this.out(pan));
    osc.start(t);
    osc.stop(t + dur + 0.02);
  }

  private blip(t: number, freq: number, dur: number, vol: number): void {
    const ctx = this.ctx!;
    const osc = ctx.createOscillator();
    osc.type = 'square';
    osc.frequency.value = freq;
    const g = ctx.createGain();
    g.gain.setValueAtTime(vol, t);
    g.gain.exponentialRampToValueAtTime(0.001, t + dur);
    osc.connect(g).connect(this.master!);
    osc.start(t);
    osc.stop(t + dur + 0.02);
  }

  private sweep(from: number, to: number, dur: number, vol: number): void {
    const ctx = this.ctx!;
    const t = ctx.currentTime;
    const osc = ctx.createOscillator();
    osc.type = 'triangle';
    osc.frequency.setValueAtTime(from, t);
    osc.frequency.exponentialRampToValueAtTime(to, t + dur);
    const g = ctx.createGain();
    g.gain.setValueAtTime(vol, t);
    g.gain.exponentialRampToValueAtTime(0.001, t + dur);
    osc.connect(g).connect(this.master!);
    osc.start(t);
    osc.stop(t + dur + 0.02);
  }
}
